// @TODO review this and fix
import { DISCARD_CRAWL } from '@components/App/SystemDialogs/constants'
import { openModal, openSystemDialog } from '@storeOld/reducers/ui'
import { useDispatch } from 'react-redux'

import { SELECT_PAGES_IF_MORE } from '@/constants/env'
import {
  type CrawlerJobId,
  type ImportSitemapData,
  type PageNameSource,
  ProjectOrigin,
} from '@/types'
import { IMPORT_SELECT_URLS } from '../../Modal/constants'
import { useCommit, useCommitIntoProject } from '../../Modal/Import/hooks'
import { useCrawlerRemoveJob } from './useCrawlerRemoveJob'

// Where a finished crawl is meant to land. Everything that can end a crawl needs it —
// normal completion and the Stop button both — so it travels as one named thing rather
// than as three arguments each caller has to remember.
export interface CrawlerJobTarget {
  teamId: string
  folderId: string | null
  // Land the crawl in the project the user already has open, as a new section, instead
  // of building a new project from it.
  intoOpenProject: boolean
  // What the landed pages are named after. Only the window that started the crawl knows;
  // a job opened from the dashboard list lands with URL names.
  pageNames: PageNameSource
}

export const useCrawlerOpenJob = (props?: Partial<CrawlerJobTarget>) => {
  const { remove } = useCrawlerRemoveJob()
  const dispatch = useDispatch()
  const intoOpenProject = Boolean(props?.intoOpenProject)

  const commitNewProject = useCommit({
    teamId: props?.teamId,
    folderId: props?.folderId,
    projectOrigin: ProjectOrigin.CRAWLED,
    popupDownloadSitemapXml: true,
  })

  const commitIntoProject = useCommitIntoProject()

  const land = (crawl: ImportSitemapData, jobId?: CrawlerJobId) => {
    if (jobId) {
      remove(jobId)
    }

    const payload = { ...crawl, pageNames: props?.pageNames ?? 'url' }

    if (intoOpenProject) {
      // Deliberately not caught here: a refusal has to reach the page-selection modal,
      // which clears its pending state in .catch. Swallowing it would leave the confirm
      // button disabled for good.
      return commitIntoProject(payload)
    }

    return commitNewProject(payload).catch(() => {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.log('error after crawler done')
    })
  }

  const confirmDiscard = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(openSystemDialog(DISCARD_CRAWL, { callback: resolve }))
    })

  const openJob = (result: ImportSitemapData, jobId?: CrawlerJobId) => {
    // Importing into a project that already has pages in it always shows what is about
    // to be added — the small-crawl shortcut only makes sense when the crawl IS the
    // whole project.
    if (intoOpenProject || result?.data?.length > SELECT_PAGES_IF_MORE) {
      dispatch(
        // @ts-expect-error
        openModal(IMPORT_SELECT_URLS, {
          // The crawl exists only in this window: a stray click beside it or Esc must not
          // close it, and its own × asks first (OCT-5313).
          isLocked: true,
          confirmClosing: confirmDiscard,
          data: result,
          handleCommit: (selected: ImportSitemapData) => land(selected, jobId),
          limitExceeded: result.limitExceeded,
          timeoutExceeded: result.timeout,
        })
      )

      return
    }

    land(result, jobId)
  }

  return { openJob }
}
